import { proposalRulesSchema } from "./condition.js";
import { labelLayoutsSchema } from "./labels.js";
import { z } from "zod";
import type { ModuleKey, ModuleLevel } from "./modules.js";
import { roleSchema } from "./enums.js";
import { domainSchema, emailSchema } from "./slug.js";
import type { Subscription } from "./platform.js";
import type { SupportRole } from "./support.js";
import type { MemberClientAccess } from "./clients.js";

/** Instellingen per organisatie (`organizations.settings`). */
/** Huisstijl van het PDF-rapport per organisatie (de app zelf is altijd Klantpunt). */
export const REPORT_BRANDS = ["klantpunt", "pandwachters"] as const;
export type ReportBrand = (typeof REPORT_BRANDS)[number];
export const REPORT_BRAND_LABELS: Record<ReportBrand, string> = {
  klantpunt: "Klantpunt",
  pandwachters: "Pandwachters",
};

export const orgSettingsSchema = z.object({
  allowed_domains: z.array(domainSchema).default([]),
  default_role: roleSchema.default("inspecteur"),
  report_brand: z.enum(REPORT_BRANDS).catch("klantpunt").default("klantpunt"),
  /** Eigen huisstijl in rapporten (docs/BRAND.md): logo (bestand van de organisatie) en accentkleur. */
  report_logo_file_id: z.uuid().nullable().catch(null).default(null),
  /** Vierkant pictogram van de organisatie (ingeklapt menu, bovenbalk op de telefoon). */
  app_icon_file_id: z.uuid().nullable().catch(null).default(null),
  report_accent: z
    .string()
    .regex(/^#[0-9A-F]{6}$/)
    .nullable()
    .catch(null)
    .default(null),
  /** "nlbe-sfb-2025" = volledige NLBE-SfB-tabel (sub- en detailcodes); anders alleen elementen. */
  element_coding: z.literal("nlbe-sfb-2025").nullable().catch(null).default(null),
  /** Dagen voor de vervaldatum: status Binnenkort en de taak *Keuring plannen* (docs/ASSETS-MJOP.md). */
  obligation_reminder_days: z.number().int().min(0).max(365).catch(60).default(60),
  /** MJOP (docs/ASSETS-MJOP.md, "Index en instellingen"); de beheerder kiest de index zelf. */
  mjop_index_percent: z.number().min(-20).max(50).catch(0).default(0),
  /** null = het lopende jaar. */
  mjop_index_base_year: z.number().int().min(1900).max(2200).nullable().catch(null).default(null),
  mjop_horizon_years: z.number().int().catch(10).default(10),
  /** Conditiemeting: voorstellen voor het vervangingsjaar per score (docs/CONDITIEMETING.md §8). */
  condition_proposal_rules: proposalRulesSchema.nullable().catch(null).default(null),
  /** Laatst gebruikte formaten voor QR-labels, nieuwste eerst (gedeeld binnen de organisatie). */
  label_layouts: labelLayoutsSchema.catch([]).default([]),
});
export type OrgSettings = z.infer<typeof orgSettingsSchema>;

export const inviteBodySchema = z.object({
  email: emailSchema,
  role: roleSchema,
  /** Gebouwen waartoe de uitgenodigde toegang krijgt (niet nodig voor eigenaar/beheerder). */
  buildingIds: z.array(z.uuid()).max(500).default([]),
});
export const memberBuildingsBodySchema = z.object({ buildingIds: z.array(z.uuid()).max(500) });
export const updateMemberBodySchema = z.object({ role: roleSchema });
export const selectOrgBodySchema = z.object({ orgId: z.uuid() });
export const loginLinkBodySchema = z.object({ email: emailSchema, returnTo: z.string().optional() });
export const loginCallbackBodySchema = z.object({ token: z.string().min(1).max(200) });
/** Antwoord op het inwisselen van een inloglink: pad binnen de app om naartoe te gaan. */
export interface LoginCallbackResponse {
  redirect: string;
}

export interface MeResponse {
  user: { id: string; email: string; name: string | null; avatarUrl: string | null; platformAdmin: boolean };
  org: {
    id: string;
    name: string;
    slug: string;
    subscription: Subscription;
    reportBrand: ReportBrand;
    /** Eigen logo (file-ID) en accentkleur voor rapporten; null = die van `reportBrand`. */
    reportLogoFileId: string | null;
    /** Vierkant pictogram (file-ID) voor kleine plekken in de app; null = het logo. */
    iconFileId?: string | null;
    reportAccent: string | null;
    /** Volledige NLBE-SfB-tabel aan (instelling `element_coding`). */
    elementCoding: boolean;
  } | null;
  role: z.infer<typeof roleSchema> | null;
  permissions: string[];
  /** Niveau per module (organisatie × gebruiker); leeg zonder organisatie. */
  modules: Partial<Record<ModuleKey, ModuleLevel>>;
  orgs: { id: string; name: string; slug: string; role: z.infer<typeof roleSchema> }[];
  /** Supportrol (helpcenter, docs/HELPCENTER.md §9); ontbreekt in oudere, bewaarde antwoorden. */
  support?: SupportRole | null;
}

export interface AuthOptions {
  google: boolean;
  mail: boolean;
}

export interface MemberRow {
  userId: string;
  email: string;
  name: string | null;
  role: z.infer<typeof roleSchema>;
  lastLoginAt: string | null;
  buildingIds: string[];
  /** Toegang per klant (rollen die niet alles zien). */
  access: MemberClientAccess[];
  /** Eigen niveau per module (instelling of rolstandaard). */
  modules: Record<ModuleKey, ModuleLevel>;
}
export interface InvitationRow {
  id: string;
  email: string;
  role: z.infer<typeof roleSchema>;
  expiresAt: string;
  buildingIds: string[];
}
